import { flags } from '../core/flags';
import { chunkStepper, type FromWorker, type MeshJob, type ToWorker } from './caveJobs';
import type { ChunkMesh } from './surfaceNets';

interface Listener {
  onChunk(mesh: ChunkMesh): void;
  onDone(chunks: number, ms: number): void;
}

/**
 * Meshes caves in a Web Worker, so the main thread never stalls on it. Falls back to
 * meshing on the main thread, a little each frame (`pump`), where workers aren't
 * available or with `?caveworker=0`.
 */
export class CaveMesher {
  private readonly worker: Worker | null;
  private readonly listeners = new Map<string, Listener>();
  /** Main-thread fallback: jobs in progress. */
  private readonly local = new Map<
    string,
    { next: ReturnType<typeof chunkStepper>; count: number; t0: number }
  >();

  constructor() {
    let worker: Worker | null = null;
    if (typeof Worker !== 'undefined' && flags.bool('caveworker', true)) {
      try {
        worker = new Worker(new URL('./caveWorker.ts', import.meta.url), { type: 'module' });
        worker.onmessage = (e: MessageEvent<FromWorker>) => this.receive(e.data);
        worker.onerror = (e) => console.error('[innerworlds] cave worker', e.message);
      } catch (err) {
        console.warn('[innerworlds] no cave worker, meshing on the main thread', err);
        worker = null;
      }
    }
    this.worker = worker;
  }

  /** Start meshing; chunks arrive through `onChunk` as they're done. */
  mesh(job: MeshJob, listener: Listener): void {
    this.listeners.set(job.key, listener);
    if (this.worker) {
      const msg: ToWorker = { type: 'mesh', job };
      this.worker.postMessage(msg);
    } else {
      this.local.set(job.key, { next: chunkStepper(job), count: 0, t0: performance.now() });
    }
  }

  cancel(key: string): void {
    this.listeners.delete(key);
    this.local.delete(key);
    if (this.worker) {
      const msg: ToWorker = { type: 'cancel', key };
      this.worker.postMessage(msg);
    }
  }

  /** Main-thread fallback only: mesh chunks until `budgetMs` is spent. */
  pump(budgetMs: number): void {
    if (this.worker || this.local.size === 0) return;
    const end = performance.now() + budgetMs;
    for (const [key, job] of this.local) {
      while (performance.now() < end) {
        const step = job.next();
        if (!step) {
          this.local.delete(key);
          this.receive({ type: 'done', key, chunks: job.count, ms: performance.now() - job.t0 });
          break;
        }
        if (step.mesh) {
          job.count++;
          this.receive({ type: 'chunk', key, mesh: step.mesh });
        }
      }
      if (performance.now() >= end) return;
    }
  }

  private receive(msg: FromWorker): void {
    const l = this.listeners.get(msg.key);
    if (!l) return;
    if (msg.type === 'chunk') l.onChunk(msg.mesh);
    else {
      this.listeners.delete(msg.key);
      l.onDone(msg.chunks, msg.ms);
    }
  }
}
